export type PieceKind = 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L';

export const PIECE_KINDS: readonly PieceKind[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];

/** Each shape always has the same color (none matches the official game's mapping). */
export const PIECE_COLORS: Record<PieceKind, string> = {
  I: '#d8402f',
  O: '#2f5fd0',
  T: '#f0b72f',
  S: '#2a2724',
  Z: '#2f9a55',
  J: '#e8762b',
  L: '#d9c7a3',
};

export type Cell = readonly [row: number, col: number];

interface Shape {
  /** Size of the square box the piece rotates in. */
  box: number;
  cells: Cell[];
}

// Spawn orientation, as offsets inside the rotation box.
const SHAPES: Record<PieceKind, Shape> = {
  I: { box: 4, cells: [[1, 0], [1, 1], [1, 2], [1, 3]] },
  O: { box: 2, cells: [[0, 0], [0, 1], [1, 0], [1, 1]] },
  T: { box: 3, cells: [[0, 1], [1, 0], [1, 1], [1, 2]] },
  S: { box: 3, cells: [[0, 1], [0, 2], [1, 0], [1, 1]] },
  Z: { box: 3, cells: [[0, 0], [0, 1], [1, 1], [1, 2]] },
  J: { box: 3, cells: [[0, 0], [1, 0], [1, 1], [1, 2]] },
  L: { box: 3, cells: [[0, 2], [1, 0], [1, 1], [1, 2]] },
};

const rotateCw = (cells: Cell[], box: number): Cell[] => cells.map(([r, c]) => [c, box - 1 - r] as const);

/** The 4 orientations of each piece (clockwise order), as offsets inside its rotation box. */
export const ROTATIONS: Record<PieceKind, Cell[][]> = PIECE_KINDS.reduce((acc, kind) => {
  const { box, cells } = SHAPES[kind];
  const out = [cells];
  for (let i = 1; i < 4; i++) out.push(kind === 'O' ? cells : rotateCw(out[i - 1], box));
  acc[kind] = out;
  return acc;
}, {} as Record<PieceKind, Cell[][]>);

/** Where a new piece's rotation box starts: top rows, horizontally centered. */
export function spawnPosition(kind: PieceKind): { row: number; col: number } {
  if (kind === 'I') return { row: -1, col: 3 };
  if (kind === 'O') return { row: 0, col: 4 };
  return { row: 0, col: 3 };
}

/** A 2×4 picture of the piece for the "next" preview, e.g. ['.T..', 'TTT.']. */
export function previewRows(kind: PieceKind): string[] {
  const cells = ROTATIONS[kind][0];
  const minR = Math.min(...cells.map(([r]) => r));
  const minC = Math.min(...cells.map(([, c]) => c));
  const width = Math.max(...cells.map(([, c]) => c)) - minC + 1;
  const shift = Math.floor((4 - width) / 2) - minC;
  const rows = [['.', '.', '.', '.'], ['.', '.', '.', '.']];
  for (const [r, c] of cells) rows[r - minR][c + shift] = kind;
  return rows.map((r) => r.join(''));
}
